import React, { useState, useRef, useEffect, useId } from 'react';
import SearchIcon from '../Icons/SearchIcon';
import DropDownIcon from '../Icons/DropDownIcon';
import styles from './CountrySelectorWithSearch.module.css';
import { fetchCountries, getCachedCountries } from '../../Utils/countryUtils';

const CountrySelectorWithSearch = ({ value, onChange, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [countries, setCountries] = useState(() => getCachedCountries() || []);
    const [isLoading, setIsLoading] = useState(() => getCachedCountries() === null);
    const [loadError, setLoadError] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isKeyboardNavActive, setIsKeyboardNavActive] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const searchInputRef = useRef(null);
    const countriesListRef = useRef(null);
    const listId = useId();

    // Fetch countries using memoized utility (avoids duplicate API calls)
    useEffect(() => {
        let active = true;
        const loadCountries = async () => {
            const cachedCountries = getCachedCountries();
            if (cachedCountries !== null) {
                setCountries(cachedCountries);
                setIsLoading(false);
                setLoadError(false);
                return;
            }

            setIsLoading(true);
            setLoadError(false);
            try {
                const fetchedCountries = await fetchCountries();
                if (active) {
                    setCountries(fetchedCountries);
                }
            } catch {
                if (active) setLoadError(true);
            } finally {
                if (active) setIsLoading(false);
            }
        };
        loadCountries();
        return () => { active = false; };
    }, [loadAttempt]);

    // Get flag image for a country (from API)
    const getFlagImage = (country) => {
        if (!country || !country.image) return null;
        return (
            <img 
                key={country.image}
                src={country.image} 
                alt=""
                width={18}
                height={18}
                decoding="async"
                className={styles.countryFlag}
                onError={(e) => {
                    e.target.style.display = 'none';
                }}
            />
        );
    };

    // Get flag image for dropdown options
    const getFlagImageOption = (country) => {
        if (!country || !country.image) return null;
        return (
            <img 
                src={country.image} 
                alt=""
                width={15}
                height={15}
                loading="lazy"
                decoding="async"
                className={styles.countryFlagOption}
                onError={(e) => {
                    e.target.style.display = 'none';
                }}
            />
        );
    };

    // Auto-scroll to selected item
    useEffect(() => {
        if (selectedIndex >= 0 && countriesListRef.current && isKeyboardNavActive) {
            const items = Array.from(countriesListRef.current.children).filter(
                child => child.classList?.contains(styles.countryOption)
            );
            items[selectedIndex]?.scrollIntoView?.({ behavior: 'auto', block: 'nearest' });
        }
    }, [selectedIndex, isKeyboardNavActive]);

    // Reset selected index when search term changes
    useEffect(() => {
        setSelectedIndex(-1);
        setIsKeyboardNavActive(false);
    }, [searchTerm]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setSelectedIndex(-1);
                setIsKeyboardNavActive(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            searchInputRef.current?.focus();
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Filter and sort countries based on search term
    const filteredCountries = React.useMemo(() => {
        if (!searchTerm.trim()) return countries;

        const search = searchTerm.toLowerCase().trim();
        const searchDigits = search.replace(/\D/g, '');

        // Filter matches
        const matched = countries.filter(country => {
            const name = (country.name || '').toLowerCase();
            const code = (country.code || '').toLowerCase();
            const dialCode = country.dial_code?.toString() || '';
            
            // Only match by digits if there are actual digits in the search
            const digitMatch = searchDigits.length > 0 && dialCode.includes(searchDigits);

            return name.includes(search) || 
                   code.includes(search) || 
                   dialCode.includes(search) || 
                   digitMatch ||
                   `+${dialCode}`.includes(search);
        });

        // Sort by priority: exact dial code > dial code starts > name starts > name contains
        return matched.sort((a, b) => {
            const aName = (a.name || '').toLowerCase();
            const bName = (b.name || '').toLowerCase();
            const aDial = a.dial_code?.toString() || '';
            const bDial = b.dial_code?.toString() || '';

            // Priority 1: Exact dial code match
            if (aDial === searchDigits && bDial !== searchDigits) return -1;
            if (aDial !== searchDigits && bDial === searchDigits) return 1;

            // Priority 2: Dial code starts with
            if (aDial.startsWith(searchDigits) && !bDial.startsWith(searchDigits)) return -1;
            if (!aDial.startsWith(searchDigits) && bDial.startsWith(searchDigits)) return 1;

            // Priority 3: Name starts with
            if (aName.startsWith(search) && !bName.startsWith(search)) return -1;
            if (!aName.startsWith(search) && bName.startsWith(search)) return 1;

            // Priority 4: Name contains (earlier = better)
            const aIdx = aName.indexOf(search);
            const bIdx = bName.indexOf(search);
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
            if (aIdx !== -1 && bIdx === -1) return -1;
            if (aIdx === -1 && bIdx !== -1) return 1;

            return 0;
        });
    }, [countries, searchTerm]);

    const handleCountrySelect = (country) => {
        // Pass the full country object with id
        onChange(country);
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
        setIsKeyboardNavActive(false);
        triggerRef.current?.focus();
    };

    // Handle keyboard navigation
    const handleSearchKeyDown = (e) => {
        if (!isOpen) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            setSearchTerm('');
            setSelectedIndex(-1);
            setIsKeyboardNavActive(false);
            triggerRef.current?.focus();
            return;
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && filteredCountries[selectedIndex]) {
                handleCountrySelect(filteredCountries[selectedIndex]);
            }
            return;
        }

        if (filteredCountries.length === 0) return;

        // Arrow Down navigation
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIsKeyboardNavActive(true);
            setSelectedIndex(prev => Math.min(prev + 1, filteredCountries.length - 1));
        }
        // Arrow Up navigation
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIsKeyboardNavActive(true);
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        }
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(open => !open);
            setSearchTerm('');
            setSelectedIndex(-1);
            setIsKeyboardNavActive(false);
        }
    };

    // Get selected country object - handle both object and code string
    const selectedCountry = value && typeof value === 'object' ? value : 
        (value ? countries.find(c => c.code === value) || null : null);
    
    const selectedCountryName = selectedCountry ? selectedCountry.name : '';
    const selectedCallingCode = selectedCountry ? `+${selectedCountry.dial_code}` : '';

    return (
        <div className={styles.countrySelectorContainer} ref={dropdownRef}>
            <button
                ref={triggerRef}
                type="button"
                className={styles.countrySelectorTrigger}
                onClick={handleToggle}
                disabled={disabled}
                aria-label={selectedCountry ? `Select country, ${selectedCountryName} ${selectedCallingCode}` : 'Select country code'}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={isOpen ? listId : undefined}
            >
                {selectedCountry && getFlagImage(selectedCountry)}
                <span className={styles.countryCode}>{selectedCallingCode || (isLoading ? '…' : 'Code')}</span>
                <DropDownIcon 
                    size={10} 
                    color="#6b7280" 
                    className={`${styles.dropdownIcon} ${isOpen ? styles.rotated : ''}`}
                />
            </button>

            {isOpen && (
                <div className={styles.countryDropdown}>
                    {/* Search Input */}
                    <div className={styles.searchContainer}>
                        <SearchIcon size={16} color="#6b7280" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            role="combobox"
                            aria-label="Search country or calling code"
                            aria-autocomplete="list"
                            aria-expanded={isOpen}
                            aria-controls={listId}
                            aria-activedescendant={selectedIndex >= 0 && filteredCountries[selectedIndex] ? `${listId}-${filteredCountries[selectedIndex].id}` : undefined}
                            placeholder="Search country or code..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            className={styles.searchInput}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    {/* Countries List */}
                    <div 
                        ref={countriesListRef}
                        id={listId}
                        role="listbox"
                        aria-label="Countries"
                        aria-busy={isLoading}
                        className={`${styles.countriesList} ${isKeyboardNavActive ? styles.keyboardNavActive : ''}`}
                    >
                        {isLoading ? (
                            <div className={styles.noResults} role="status">
                                Loading countries...
                            </div>
                        ) : loadError ? (
                            <div className={styles.noResults} role="alert">
                                <span>Unable to load countries.</span>
                                <button
                                    type="button"
                                    className={styles.retryButton}
                                    onClick={() => {
                                        setLoadAttempt(attempt => attempt + 1);
                                        searchInputRef.current?.focus();
                                    }}
                                >
                                    Retry
                                </button>
                            </div>
                        ) : filteredCountries.length > 0 ? (
                            filteredCountries.map((country, index) => {
                                const countryName = country.name || '';
                                const callingCode = country.dial_code ? `+${country.dial_code}` : '';
                                const isSelected = selectedCountry && selectedCountry.id === country.id;
                                const isHighlighted = selectedIndex === index;

                                return (
                                    <div
                                        key={country.id}
                                        id={`${listId}-${country.id}`}
                                        role="option"
                                        aria-selected={Boolean(isSelected)}
                                        className={`${styles.countryOption} ${isSelected ? styles.selected : ''} ${isHighlighted ? styles.selectedCountryItem : ''}`}
                                        onClick={() => {
                                            setIsKeyboardNavActive(false);
                                            handleCountrySelect(country);
                                        }}
                                        onMouseEnter={() => {
                                            if (!isKeyboardNavActive) {
                                                setSelectedIndex(index);
                                            }
                                        }}
                                        onMouseMove={() => {
                                            if (isKeyboardNavActive) {
                                                setIsKeyboardNavActive(false);
                                                setSelectedIndex(index);
                                            }
                                        }}
                                    >
                                        {getFlagImageOption(country)}
                                        <span className={styles.countryName}>{countryName}</span>
                                        <span className={styles.countryCallingCode}>{callingCode}</span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles.noResults} role="status">
                                No country available
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CountrySelectorWithSearch;
